import React, { useEffect, useState } from "react";
import { Maximize2 } from "lucide-react";
import axios from "axios";
import heartLoading from "../../../assests/heartLoading.gif";
import KpiLineage from "../Domain/KpiLineage";

/**
 * Fetches /kpi-all-products?kpi_name=... and shows a combined lineage graph
 * for all products, assets, and columns linked to a KPI.
 */
export default function GlossaryKpiLineage({ kpiName }) {
  const [loading, setLoading] = useState(true);
  const [graphData, setGraphData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!kpiName) return;
    setLoading(true);
    setError(null);

    axios
      .get(`${process.env.REACT_APP_API_URL}/kpi-all-products`, {
        params: { kpi_name: kpiName },
      })
      .then((res) => {
        const products = res.data?.products;
        if (!Array.isArray(products) || products.length === 0) {
          setGraphData(null);
          setLoading(false);
          return;
        }

        // API now returns only the requested KPI with columns (downstreams) flattened
        setGraphData({ products });
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load KPI lineage data.");
        setLoading(false);
      });
  }, [kpiName]);

  const getFullScreenUrl = () => {
    if (!kpiName) return "";
    const params = new URLSearchParams();
    params.set("mode", "glossary");
    params.set("kpiName", kpiName);
    return `/kpi-lineage-fullscreen?${params.toString()}`;
  };

  if (loading) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ height: "55vh" }}
      >
        <img src={heartLoading} alt="Loading..."style={{ width : 200}} />
        <span className="text-gray-700 text-lg mt-3">Loading KPI lineage...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center py-8 text-sm">{error}</div>
    );
  }

  if (!graphData) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ height: "40vh" }}
      >
        <svg
          className="w-14 h-14 text-gray-300 mb-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 17v-2m3 2v-4m3 4v-6M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <p className="text-gray-500 font-medium">No KPI Lineage Data Available</p>
        <p className="text-gray-400 text-sm mt-1">
          No lineage information found for this KPI.
        </p>
      </div>
    );
  }

  // Pass pre-built nodes/edges to KpiLineage via its raw mode
  return (
    <div className="flex flex-col h-full" style={{ width: "100%", position: "relative" }}>
      {kpiName && (
        <div className="flex items-center gap-2 shrink-0 border-b border-slate-200 px-2 py-2">
          <a
            href={getFullScreenUrl()}
            target="_self"
            title="View in full screen"
            aria-label="View in full screen"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap border border-blue-200 bg-blue-50 text-blue-700 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-100"
          >
            <Maximize2 className="h-4 w-4" />
            <span>Full screen</span>
          </a>
        </div>
      )}
      <div style={{ width: "100%", height: "55vh", minHeight: "30vh", position: "relative", flex: 1 }}>
        <KpiLineage data={graphData} loading={false} lineageMode="product-asset-column" />
      </div>
    </div>
  );
}
